import React, {Fragment, useState} from 'react';
import {Link} from 'react-router-dom';
import {Controller, useForm} from 'react-hook-form';

import Button from 'components/Button';
import TextField from 'components/TextField';
import Typography from 'components/Typography';
import {validateEmail} from 'utils/validators';
import useAuth from 'hooks/useAuth';
import EyeIcon from '../EmployeeForm/icons/eye.svg';
import EyeDisabledIcon from '../EmployeeForm/icons/eye-disable.svg';
import styles from './NewSignup.module.scss';
import toast, {Toaster} from 'react-hot-toast';
import api from 'api';
import {get} from 'lodash';
import ConfirmationModal from './ConfirmationModal';
import {CompanyType} from 'types';
import {
  BuildingIcon,
  CheckIcon,
  WrenchIcon,
  TruckIcon,
  CpuIcon,
  PackageIcon,
} from './icons';
import classNames from 'classnames';
import Layout from 'components/Layout';
import Select from 'components/Select';
import Logo from 'components/Logo';
import {COMPANY_TYPE_OPTIONS} from 'constants/select-options';

interface FormValues {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  passwordConfirmation: string;
  company_name: string;
  company_type: {
    label: string;
    value: CompanyType;
  };
}

const featureContent: Record<
  CompanyType,
  {
    icon: React.ElementType;
    title: string;
    description: string;
    features: string[];
  }
> = {
  property_manager: {
    icon: BuildingIcon,
    title: 'For Property Managers',
    description:
      'Empower your tenants with smart request management while maintaining complete control over job dispatch.',
    features: [
      'Tenant mobile app for easy request submission',
      'AI-powered automated dispatch to the best providers',
      'Real-time job tracking for you and your tenants',
      'Consolidated invoicing and reporting',
    ],
  },
  service_provider: {
    icon: WrenchIcon,
    title: 'For Field Service Providers',
    description:
      'Receive, track, and complete jobs seamlessly with our powerful mobile platform.',
    features: [
      'Instant job alerts that match your skills',
      'GPS navigation and AI-optimized routing',
      'Digital invoicing and instant payment collection',
      'Performance analytics to grow your business',
    ],
  },
  delivery_manager: {
    icon: TruckIcon,
    title: 'For Delivery Companies',
    description:
      'Optimize routes, track deliveries, and collect payments to streamline your entire delivery operation.',
    features: [
      'AI route optimization to save time and fuel',
      'Real-time delivery tracking for customer satisfaction',
      'Proof of delivery and contactless payments',
      'Driver performance and fleet management tools',
    ],
  },
  msp_provider: {
    icon: CpuIcon,
    title: 'For Managed Service Providers',
    description:
      'Empower your customers with smart request management while maintaining complete control over job dispatch.',
    features: [
      'Customer mobile app for easy request submission',
      'Real-time job tracking for you and your customers',
      'Advanced invoicing and reporting',
    ],
  },
  pickup_dropoff: {
    icon: PackageIcon,
    title: 'For Reseller Companies',
    description:
      'Save time and avoid the hassle: we bring the courier service to your door with our convenient pick up and drop off options.',
    features: [
      'Monitor parcel location with live tracking.',
      'Customers select flexible pick-up drop-off times.',
      'Drivers capture digital proof of delivery.',
      'AI optimizes all our delivery routes.',
    ],
  },
};

const backgroundClasses = {
  property_manager: styles.propertyManagerBackground,
  service_provider: styles.serviceProviderBackground,
  delivery_manager: styles.deliveryManagerBackground,
  msp_provider: styles.mspProviderBackground,
  pickup_dropoff: styles.pickupDropoffBackground,
};
const companyTypeCards = [
  {
    key: 'property_manager',
    title: 'Property Managers',
    description: 'Tenant integration & automated dispatch',
  },
  {
    key: 'service_provider',
    title: 'Field Service',
    description: 'Job tracking & performance analytics',
  },
  {
    key: 'delivery_manager',
    title: 'Delivery',
    description: 'Route optimization & payment collection',
  },
  {
    key: 'msp_provider',
    title: 'MSP / IT',
    description: 'Manage your MSP business with ease',
  },
  {
    key: 'pickup_dropoff',
    title: 'Reseller',
    description: 'Save time and avoid the hassle',
  },
];

function Signup() {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showPassword1, setShowPassword1] = useState(false);
  const [confirmationVisible, setConfirmationVisible] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    getValues,
    setValue,
    control,
    formState: {errors},
  } = useForm<FormValues>({});

  const {register: signup} = useAuth();

  async function submit(data: FormValues) {
    setLoading(true);

    try {
      await signup({...data, company_type: data.company_type.value});
      toast.success('Email confirmed');
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }

  const confirmHandler = async (value: string) => {
    try {
      await api.post('/companies/verify-code/', {
        code: value,
        email: getValues('email'),
      });
      handleSubmit(submit)();
      setLoading(false);
      setConfirmationVisible(false);
    } catch (error) {
      toast.error(
        get(error, 'response.data.error', '') || 'Something went wrong'
      );
      setLoading(false);
    }
  };

  const sendConfirmation = async () => {
    try {
      await api.post('/companies/send-code/', {
        email: getValues('email'),
      });
      toast.success('Confirmation code sent to your email');
      setConfirmationVisible(true);
    } catch (error) {
      toast.error(
        get(error, 'response.data.error', '') || 'Something went wrong'
      );
    }
  };

  const resendCode = () => {
    sendConfirmation();
  };
  const selectedCompanyType = watch('company_type');
  const FeatureIcon =
    featureContent[selectedCompanyType?.value as CompanyType]?.icon;
  const backgroundClass =
    backgroundClasses[selectedCompanyType?.value as CompanyType];
  return (
    <Layout>
      <div className={styles.container}>
        <div className={styles.formSection}>
          <div className={styles.formCard}>
            <div className={styles.formCardContent}>
              <Typography variant="h1" className={styles.title}>
                Create Your Account
              </Typography>
              <Typography variant="p" className={styles.subtitle}>
                Let's get started with Magic Dispatch.
              </Typography>

              <div className={styles.companyTypeSection}>
                <div className={styles.companyTypeGrid}>
                  {companyTypeCards.map((card) => (
                    <button
                      key={card.key}
                      type="button"
                      onClick={() => {
                        const selectedOption = COMPANY_TYPE_OPTIONS.find(
                          (option) => option.value === card.key
                        );
                        if (selectedOption) {
                          setValue('company_type', {
                            label: selectedOption.label as string,
                            value: selectedOption.value as CompanyType,
                          });
                        }
                      }}
                      className={classNames(styles.companyTypeCard, {
                        [styles.propertyManager]:
                          card.key === 'property_manager',
                        [styles.serviceProvider]:
                          card.key === 'service_provider',
                        [styles.deliveryManager]:
                          card.key === 'delivery_manager',
                        [styles.mspProvider]: card.key === 'msp_provider',
                        [styles.pickupDropoff]: card.key === 'pickup_dropoff',
                        [styles.selected]:
                          selectedCompanyType?.value === card.key,
                      })}
                    >
                      <h3 className={styles.companyTypeCardTitle}>
                        {card.title}
                      </h3>
                      <p className={styles.companyTypeCardDescription}>
                        {card.description}
                      </p>
                    </button>
                  ))}
                </div>
              </div>
              <Fragment>
                <form onSubmit={handleSubmit(submit)} className={styles.form}>
                  <TextField
                    label="Company Name"
                    placeholder="Your Company LLC"
                    error={errors.company_name?.message}
                    {...register('company_name', {
                      required: 'Required',
                    })}
                  />
                  <Controller
                    control={control}
                    rules={{required: 'Required'}}
                    name="company_type"
                    render={({field: {ref, ...field}}) => (
                      <Fragment>
                        <div
                          style={{marginTop: 20, marginBottom: 10}}
                          className={styles.label}
                        >
                          Company Type
                        </div>
                        <Select
                          isSearchable={false}
                          placeholder={
                            <span className={styles.selectPlaceholder}>
                              Select company type
                            </span>
                          }
                          options={COMPANY_TYPE_OPTIONS}
                          error={errors.company_type?.message}
                          {...field}
                        />
                        {errors.company_type?.message && (
                          <Typography className={styles.error} variant="p">
                            {errors?.company_type?.message}
                          </Typography>
                        )}
                      </Fragment>
                    )}
                  />

                  <TextField
                    label="Email"
                    placeholder="you@company.com"
                    type="email"
                    error={errors.email?.message}
                    {...register('email', {
                      required: 'Required',
                      validate: validateEmail,
                    })}
                  />
                  <div className={styles.row}>
                    <TextField
                      wrapperClass={styles.input}
                      label="First Name"
                      placeholder="John"
                      error={errors.first_name?.message}
                      {...register('first_name', {
                        required: 'Required',
                      })}
                    />
                    <TextField
                      wrapperClass={styles.input}
                      label="Last Name"
                      placeholder="Doe"
                      error={errors.last_name?.message}
                      {...register('last_name', {
                        required: 'Required',
                      })}
                    />
                  </div>

                  <div style={{position: 'relative'}}>
                    <TextField
                      label="Password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Password"
                      error={errors.password?.message}
                      {...register('password', {
                        required: 'Required',
                        minLength: {value: 6, message: 'Too short'},
                      })}
                    />
                    <img
                      onClick={() => setShowPassword((pr) => !pr)}
                      className={styles.eyeIcon}
                      src={showPassword ? EyeIcon : EyeDisabledIcon}
                      alt="eye"
                    />
                  </div>
                  <div style={{position: 'relative'}}>
                    <TextField
                      label="Confirm Password"
                      type={showPassword1 ? 'text' : 'password'}
                      placeholder="Confirm Password"
                      error={errors.passwordConfirmation?.message}
                      {...register('passwordConfirmation', {
                        required: 'Required',
                        validate: (val: string) => {
                          if (watch('password') !== val) {
                            return 'Your passwords do no match';
                          }
                        },
                      })}
                    />
                    <img
                      onClick={() => setShowPassword1((pr) => !pr)}
                      className={styles.eyeIcon}
                      src={showPassword1 ? EyeIcon : EyeDisabledIcon}
                      alt="eye"
                    />
                  </div>
                  <div className={styles.button_container}>
                    <Button
                      onClick={handleSubmit(sendConfirmation)}
                      loading={loading}
                      className={styles.signupButton}
                    >
                      <span>Sign Up</span>
                    </Button>
                  </div>
                </form>
                <div className={styles.signup}>
                  <Typography variant="p">
                    Already have an account?{' '}
                    <Link className={styles.link} to="/signin">
                      Sign In
                    </Link>
                  </Typography>
                </div>
              </Fragment>
            </div>
          </div>
        </div>

        {!!selectedCompanyType ? (
          <div className={classNames(styles.benefitsSection, backgroundClass)}>
            <div className={styles.benefitsContent}>
              <div className={styles.benefitsHeader}>
                <div className={styles.benefitsIcon}>
                  {FeatureIcon && <FeatureIcon />}
                </div>
                <Typography variant="h2" className={styles.benefitsTitle}>
                  {
                    featureContent[selectedCompanyType?.value as CompanyType]
                      ?.title
                  }
                </Typography>
              </div>
              <Typography variant="p" className={styles.benefitsDescription}>
                {
                  featureContent[selectedCompanyType?.value as CompanyType]
                    ?.description
                }
              </Typography>
              <div className={styles.benefitsList}>
                {featureContent[
                  selectedCompanyType?.value as CompanyType
                ]?.features.map((feature, index) => (
                  <div key={index} className={styles.benefitItem}>
                    <div className={styles.checkmark}>
                      <CheckIcon />
                    </div>
                    <Typography className={styles.featureItem} variant="p">
                      {feature}
                    </Typography>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div
            className={classNames(
              styles.benefitsSection,
              styles.defaultBackground
            )}
          >
            <div className={styles.defaultBenefitsContent}>
              <div className={styles.logoIcon}>
                <Logo style={{width: 60, height: 60, filter: 'invert(0)'}} />
              </div>
              <Typography variant="h3" className={styles.defaultBenefitsTitle}>
                Welcome to Magic Dispatch
              </Typography>
              <Typography
                variant="p"
                className={styles.defaultBenefitsDescription}
              >
                Select your company type to see features tailored just for you.
              </Typography>
            </div>
          </div>
        )}
      </div>

      <Toaster />
      <ConfirmationModal
        email={getValues('email')}
        confirmHandler={confirmHandler}
        resendCode={resendCode}
        confirmationVisible={confirmationVisible}
        onClose={() => setConfirmationVisible(false)}
      />
    </Layout>
  );
}

export default Signup;
